import {
  Cell,
  Pie,
  PieChart,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  LabelList,
} from "recharts";

const convertToPieData = (data) => {
  return data.map((item) => ({
    name: item.type,
    value: item.total,
  }));
};

const createTeachingLevelData = (data) => {
  const levels = ["PRE_PRIMARY", "PRIMARY", "SECONDARY"];
  const result = [];

  levels.forEach((level) => {
    const levelData = data.filter(
      (item) =>
        item.teachingLevel === level &&
        item.qualificationLevel === "All Qualifications"
    );

    const internalItem = levelData.find((item) => item.type === "Internal") || {
      male: 0,
      female: 0,
      total: 0,
    };
    const externalItem = levelData.find((item) => item.type === "External") || {
      male: 0,
      female: 0,
      total: 0,
    };

    result.push({
      name: level
        .replace("_", "-")
        .toLowerCase()
        .replace(/^\w/, (c) => c.toUpperCase()),
      internal: internalItem.total,
      external: externalItem.total,
      internalMale: internalItem.male,
      internalFemale: internalItem.female,
      externalMale: externalItem.male,
      externalFemale: externalItem.female,
      total: internalItem.total + externalItem.total,
    });
  });

  return result;
};

const createQualificationData = (data) => {
  const qualifications = ["A0", "A1", "A2"];
  const result = [];

  qualifications.forEach((qualification) => {
    const qualificationData = data.filter(
      (item) =>
        item.qualificationLevel === qualification &&
        item.teachingLevel === "All Levels"
    );

    const internalItem = qualificationData.find(
      (item) => item.type === "Internal"
    ) || {
      male: 0,
      female: 0,
      total: 0,
    };
    const externalItem = qualificationData.find(
      (item) => item.type === "External"
    ) || {
      male: 0,
      female: 0,
      total: 0,
    };

    result.push({
      name: qualification,
      internal: internalItem.total,
      external: externalItem.total,
      internalMale: internalItem.male,
      internalFemale: internalItem.female,
      externalMale: externalItem.male,
      externalFemale: externalItem.female,
      total: internalItem.total + externalItem.total,
    });
  });

  return result;
};

const calculateTotals = (data) => {
  const allData = data.filter(
    (item) =>
      item.teachingLevel === "All Levels" &&
      item.qualificationLevel === "All Qualifications"
  );

  const internal = allData.find((item) => item.type === "Internal") || {
    total: 0,
    male: 0,
    female: 0,
  };
  const external = allData.find((item) => item.type === "External") || {
    total: 0,
    male: 0,
    female: 0,
  };

  return {
    total: internal.total + external.total,
    internal: internal.total,
    external: external.total,
    internalMale: internal.male,
    internalFemale: internal.female,
    externalMale: external.male,
    externalFemale: external.female,
    male: internal.male + external.male,
    female: internal.female + external.female,
  };
};

export default function TransferTypeChart({ data }) {
  const pieData = convertToPieData(
    data.filter(
      (item) =>
        item.teachingLevel === "All Levels" &&
        item.qualificationLevel === "All Qualifications"
    )
  );

  const teachingLevelData = createTeachingLevelData(data);
  const qualificationData = createQualificationData(data);
  const totals = calculateTotals(data);

  const renderCustomBarLabel = (props) => {
    const { x, y, width, height, value, index } = props;

    if (!value) return null;

    return (
      <text
        x={x + width + 5}
        y={y + height / 1.8}
        fill="black"
        textAnchor="start"
        dominantBaseline="middle"
        fontWeight="bold"
        fontSize={14}
      >
        {value.toLocaleString()}
      </text>
    );
  };

  const TotalBox = ({ title, totalCount, color }) => (
    <div
      style={{
        flex: 1,
        border: "1px solid #eee",
        borderRadius: "8px",
        paddingTop: "15px",
        paddingBottom: "15px",
        paddingLeft: "15px",
        paddingRight: "15px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontWeight: "bold",
          fontSize: "16px",
          marginBottom: "10px",
          color: color,
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontWeight: "bold",
          fontSize: "24px",
          color: color,
        }}
      >
        {totalCount.toLocaleString()}
      </div>
    </div>
  );

  const GenderDistributionLayout = ({ children }) => (
    <div style={{ marginTop: "20px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
        {children}
      </div>
    </div>
  );

  const SectionTitle = ({ title }) => (
    <div
      style={{
        fontWeight: "bold",
        fontSize: "18px",
        marginBottom: "15px",
        padding: "8px 15px",
        backgroundColor: "#f0f0f0",
        borderLeft: "4px solid #3168c5",
        borderRadius: "0 4px 4px 0",
      }}
    >
      {title}
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "20px",
        }}
      >
        <div>
          <SectionTitle title="Teacher Transfers Overview" />
          <div style={{ height: "300px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="name"
                  label={({ name, value, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={`hsl(var(--chart-${index + 1}))`}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, label) => [
                    `${value?.toLocaleString()} ${
                      value == 1 ? "Transfer" : "Transfers"
                    }`,
                    label,
                  ]}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div
            style={{
              width: "100%",
              height: "1px",
              backgroundColor: "#e0e0e0",
              margin: "20px 0 10px 0",
            }}
          />

          <GenderDistributionLayout>
            <div style={{ display: "flex", gap: "15px" }}>
              <TotalBox
                title="Total"
                totalCount={totals.total}
                color="#3168c5"
              />

              <TotalBox
                title="Internal"
                totalCount={totals.internal}
                color="hsl(var(--chart-1))"
              />

              <TotalBox
                title="External"
                totalCount={totals.external}
                color="hsl(var(--chart-2))"
              />
            </div>
          </GenderDistributionLayout>
        </div>

        <div>
          <SectionTitle title="Distribution by Teaching Level" />
          <div style={{ height: "300px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={teachingLevelData}
                layout="vertical"
                margin={{ top: 20, right: 40, left: 40, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis
                  dataKey="name"
                  type="category"
                  tick={{
                    fontSize: 14,
                    fontWeight: "bold",
                    textTransform: "capitalize",
                  }}
                />
                <Tooltip
                  formatter={(value, name) => {
                    return [
                      `${value.toLocaleString()} ${
                        value === 1 ? "Transfer" : "Transfers"
                      }`,
                      name,
                    ];
                  }}
                />
                <Legend />
                <Bar
                  dataKey="internal"
                  name="Internal"
                  stackId="a"
                  fill="hsl(var(--chart-1))"
                  barSize={50}
                />
                <Bar
                  dataKey="external"
                  name="External"
                  stackId="a"
                  fill="hsl(var(--chart-2))"
                  barSize={50}
                >
                  <LabelList content={renderCustomBarLabel} position="right" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div
            style={{
              width: "100%",
              height: "1px",
              backgroundColor: "#e0e0e0",
              margin: "20px 0 10px 0",
            }}
          />

          <GenderDistributionLayout>
            <div style={{ display: "flex", gap: "15px" }}>
              <TotalBox
                title={teachingLevelData[0]?.name}
                totalCount={teachingLevelData[0]?.total || 0}
                color="#9C27B0"
              />

              <TotalBox
                title={teachingLevelData[1]?.name}
                totalCount={teachingLevelData[1]?.total || 0}
                color="#FF9800"
              />

              <TotalBox
                title={teachingLevelData[2]?.name}
                totalCount={teachingLevelData[2]?.total || 0}
                color="#4CAF50"
              />
            </div>
          </GenderDistributionLayout>
        </div>

        <div>
          <SectionTitle title="Distribution by Qualification" />
          <div style={{ height: "300px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={qualificationData}
                layout="vertical"
                margin={{ top: 20, right: 50, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis
                  dataKey="name"
                  type="category"
                  tick={{ fontSize: 14, fontWeight: "bold" }}
                />
                <Tooltip
                  formatter={(value, name) => {
                    return [
                      `${value.toLocaleString()} ${
                        value === 1 ? "Transfer" : "Transfers"
                      }`,
                      name,
                    ];
                  }}
                />
                <Legend />
                <Bar
                  dataKey="internal"
                  name="Internal"
                  stackId="a"
                  fill="hsl(var(--chart-1))"
                  barSize={50}
                />
                <Bar
                  dataKey="external"
                  name="External"
                  stackId="a"
                  fill="hsl(var(--chart-2))"
                  barSize={50}
                >
                  <LabelList content={renderCustomBarLabel} position="right" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div
            style={{
              width: "100%",
              height: "1px",
              backgroundColor: "#e0e0e0",
              margin: "20px 0 10px 0",
            }}
          />

          <GenderDistributionLayout>
            <div style={{ display: "flex", gap: "15px" }}>
              <TotalBox
                title={qualificationData[0]?.name}
                totalCount={qualificationData[0]?.total || 0}
                color="#2196F3"
              />

              <TotalBox
                title={qualificationData[1]?.name}
                totalCount={qualificationData[1]?.total || 0}
                color="#673AB7"
              />

              <TotalBox
                title={qualificationData[2]?.name}
                totalCount={qualificationData[2]?.total || 0}
                color="#E91E63"
              />
            </div>
          </GenderDistributionLayout>
        </div>
      </div>
    </div>
  );
}
